/**
 * Age verification — first-party browser storage only (GDPR-friendly).
 *
 * Stored record shape (no PII):
 * { v: 1, shopId, scope, verifiedAt, expiresAt?, productIds? }
 * Date of birth is validated in-memory only and never persisted.
 */
(function () {
  const STORAGE_VERSION = 1;

  function parseConfig(el) {
    const script = document.getElementById('AgeVerificationConfig-' + el.dataset.sectionId);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function storageKey(config) {
    return config.storageKey || 'gp_age_verification_v1';
  }

  function readRecord(config) {
    const key = storageKey(config);
    const raw =
      config.storage === 'session'
        ? sessionStorage.getItem(key)
        : localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeRecord(config, record) {
    const key = storageKey(config);
    const payload = JSON.stringify(record);
    if (config.storage === 'session') {
      sessionStorage.setItem(key, payload);
    } else {
      localStorage.setItem(key, payload);
    }
  }

  function clearRecord(config) {
    const key = storageKey(config);
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }

  function isExpired(record) {
    if (!record.expiresAt) return false;
    return Date.now() > new Date(record.expiresAt).getTime();
  }

  function isVerified(config) {
    const record = readRecord(config);
    if (!record || record.v !== STORAGE_VERSION) return false;
    if (String(record.shopId) !== String(config.shopId)) return false;
    if (isExpired(record)) {
      clearRecord(config);
      return false;
    }

    if (config.scope === 'per_product') {
      const ids = (record.productIds || []).map(String);
      return ids.includes(String(config.productId));
    }

    return record.scope === 'all_listed';
  }

  function saveVerification(config) {
    const now = new Date();
    const record = {
      v: STORAGE_VERSION,
      shopId: config.shopId,
      scope: config.scope,
      verifiedAt: now.toISOString(),
    };

    if (config.storage === 'local' && config.retentionDays > 0) {
      const expires = new Date(now);
      expires.setDate(expires.getDate() + config.retentionDays);
      record.expiresAt = expires.toISOString();
    }

    if (config.scope === 'per_product') {
      const existing = readRecord(config);
      const ids = new Set(
        (existing && existing.productIds ? existing.productIds : []).map(String)
      );
      ids.add(String(config.productId));
      record.productIds = Array.from(ids);
    }

    writeRecord(config, record);
  }

  function ageFromBirthdate(value) {
    const birth = new Date(value);
    if (Number.isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age;
  }

  function lockScroll() {
    document.documentElement.classList.add('age-verification--locked');
  }

  function unlockScroll() {
    document.documentElement.classList.remove('age-verification--locked');
  }

  function trapFocus(dialog, root, abortSignal) {
    const focusable = dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    root.addEventListener(
      'keydown',
      function (e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      },
      { signal: abortSignal }
    );
  }

  function hideGate(root) {
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.classList.remove('is-active');
    unlockScroll();
  }

  function showGate(root, config) {
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    root.classList.add('is-active');
    lockScroll();

    const dialog = root.querySelector('[data-age-dialog]');
    const confirmBtn = root.querySelector('[data-age-confirm]');
    const declineLink = root.querySelector('[data-age-decline]');
    const birthdateInput = root.querySelector('[data-age-birthdate]');
    const errorEl = root.querySelector('[data-age-error]');

    if (dialog) {
      dialog.focus();
    }

    const abortController = new AbortController();
    root._ageVerificationAbort = abortController;
    const { signal } = abortController;

    if (dialog) {
      trapFocus(dialog, root, signal);
    }

    if (confirmBtn) {
      confirmBtn.addEventListener(
        'click',
        function () {
          if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = '';
          }

          if (config.requireBirthdate && birthdateInput) {
            if (!birthdateInput.value) {
              if (errorEl) {
                errorEl.textContent = 'Please enter your date of birth.';
                errorEl.hidden = false;
              }
              birthdateInput.focus();
              return;
            }
            const age = ageFromBirthdate(birthdateInput.value);
            if (age === null || age < config.minimumAge) {
              if (errorEl) {
                errorEl.textContent =
                  'You must be at least ' + config.minimumAge + ' years old to continue.';
                errorEl.hidden = false;
              }
              birthdateInput.focus();
              return;
            }
          }

          saveVerification(config);
          hideGate(root);
        },
        { signal }
      );
    }

    if (declineLink) {
      declineLink.addEventListener(
        'click',
        function () {
          clearRecord(config);
        },
        { signal }
      );
    }
  }

  function teardownGate(root) {
    if (root._ageVerificationAbort) {
      root._ageVerificationAbort.abort();
      root._ageVerificationAbort = null;
    }
    hideGate(root);
  }

  function isDesignMode() {
    return window.Shopify && window.Shopify.designMode === true;
  }

  function initElement(root) {
    if (!root || root.dataset.ageInitialized === 'true') return;

    const config = parseConfig(root);
    if (!config) return;

    root.dataset.ageInitialized = 'true';
    teardownGate(root);

    if (!isDesignMode() && isVerified(config)) {
      return;
    }

    showGate(root, config);
  }

  function initAll() {
    document.querySelectorAll('age-verification').forEach(function (root) {
      root.dataset.ageInitialized = 'false';
      initElement(root);
    });
  }

  class AgeVerification extends HTMLElement {
    connectedCallback() {
      initElement(this);
    }

    disconnectedCallback() {
      teardownGate(this);
      this.dataset.ageInitialized = 'false';
    }
  }

  if (!customElements.get('age-verification')) {
    customElements.define('age-verification', AgeVerification);
  }

  initAll();

  window.addEventListener('load', initAll);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      initAll();
    }
  });

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', initAll, { once: true });
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target && event.target.querySelector
      ? event.target.querySelector('age-verification')
      : null;
    if (root) {
      root.dataset.ageInitialized = 'false';
      initElement(root);
    } else {
      initAll();
    }
  });
})();
